<template>
  <v-dialog
    :value="opened"
    width="800"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    persistent
    @keydown.esc="$emit('update:opened', false)"
  >
    <v-card>
      <v-card-title class="title">
        <b>{{ !isEmpty(title) ? title : 'Identity Verification via SMS' }} </b>
        <v-spacer></v-spacer>
        <v-btn small icon @click="$emit('update:opened', false)">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text v-if="checkingIdentityVerifications">
        <v-progress-linear indeterminate color="primary"></v-progress-linear>
      </v-card-text>

      <v-card-text v-else>
        <v-layout column>
          <v-flex>
            <div class="text-xs-left">
              Important: Please only use a verified phone number for the
              taxpayer to ensure the legitimacy of the transaction. If you have
              a prior history with the taxpayer and have communicated with them
              using the provided phone number in previous years, you should
              request a recent phone bill that displays both the phone number
              and address. This step contributes to a thorough and legally sound
              identification process.
            </div>
          </v-flex>

          <v-flex
            v-for="(identityVerification, index) in identityVerifications"
            :key="identityVerification.id"
            class="mt-3"
          >
            <div class="text-xs-left">
              <h3>
                {{ identityVerification.name }} ({{
                  identityVerification.title
                }}) <span class="error--text">*</span>
              </h3>
            </div>

            <v-text-field
              label="Phone Number"
              required
              box
              mask="phone"
              class="mt-1"
              v-model="identityVerification.phone_number"
            />
          </v-flex>
        </v-layout>

        <v-alert
          type="error"
          outline
          :value="containsSamePhoneNumbers && !hasEmptyPhoneNumbers"
          class="mt-3 text-xs-left"
        >
          <b
            >Error: Every phone number must be unique for identity
            verification.</b
          >
        </v-alert>
      </v-card-text>

      <v-card-actions class="mx-2">
        <v-checkbox
          v-model="confirmed"
          :disabled="containsSamePhoneNumbers || hasEmptyPhoneNumbers"
          label="I have confirmed the phone number(s) owned by the individuals listed above."
        />

        <v-btn color="primary" @click="save" :disabled="isSaveDisabled">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import TaxReturnIdentityVerificationMixin from '@/mixins/TaxReturnIdentityVerificationMixin';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';

export default {
  mixins: [TaxReturnIdentityVerificationMixin],

  props: {
    opened: {
      type: Boolean,
    },

    companyId: {
      type: Number,
    },

    taxReturnId: {
      type: Number,
    },

    title: {
      type: String,
      optional: true,
    },
  },

  data() {
    return {
      isEmpty,

      saving: false,
      confirmed: false,
      identityVerifications: [],
    };
  },

  computed: {
    containsSamePhoneNumbers() {
      const allPhoneNumbers = this.identityVerifications.map(
        identityVerification => {
          return identityVerification.phone_number;
        },
      );

      return uniq(allPhoneNumbers).length !== allPhoneNumbers.length;
    },

    hasEmptyPhoneNumbers() {
      return this.identityVerifications.some(identityVerification => {
        return isEmpty(identityVerification.phone_number);
      });
    },

    isSaveDisabled() {
      return !this.confirmed || this.saving || this.hasEmptyPhoneNumbers;
    },
  },

  methods: {
    async save() {
      try {
        this.saving = true;

        await Promise.all(
          this.identityVerifications.map(identityVerification => {
            return axios.patch(
              `/tax_returns/${this.taxReturnId}/tax_return_identity_verifications/${identityVerification.id}`,
              {
                company_id: this.companyId,
                phone_number: identityVerification.phone_number,
                phone_number_verified: this.confirmed,
              },
            );
          }),
        );

        this.$emit('update:opened', false);
      } catch (e) {
        console.log(e);
      } finally {
        this.saving = false;
      }
    },
  },

  watch: {
    async opened() {
      if (this.opened) {
        await this.checkIdentityVerificationsForUpdate(
          this.taxReturnId,
          this.companyId,
        );
      }
    },
  },
};
</script>

<style scoped>
.title {
  border-bottom: 1px solid var(--gray-300);
}
</style>
